<script setup>
  import {computed, inject, onMounted, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentPatientSidebarDegreeOfCareAttribute from "@components/Sidebars/PatientSidebar/DegreeOfCareAttribute.vue";
  import ComponentPatientSidebarMobilityAttribute from "@components/Sidebars/PatientSidebar/MobilityAttribute.vue";
  import ComponentPatientSidebarNursingHomeAttribute from "@components/Sidebars/PatientSidebar/NursingHomeAttribute.vue";
  import ComponentPatientSidebarPatientAddressAttribute from "@components/Sidebars/PatientSidebar/PatientAddressAttribute.vue";
  import ComponentPatientSidebarSimpleAttribute from "@components/Sidebars/PatientSidebar/SimpleAttribute.vue";
  import ComponentPatientSidebarTubePatientAttribute from "@components/Sidebars/PatientSidebar/TubePatientAttribute.vue";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {get} from "es-toolkit/compat";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "ComponentSolveConflictingPatientData",
  });

  const props = defineProps({
    sourcePatient: {
      type: Object,
      required: true,
    },
    targetPatient: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(["solved"]);

  const privacy = inject("$privacy");

  const isSolved = ref(false);

  const conflicts = ref([]);

  // note: we have to use useAuthHttp instead of router here, because we'll send
  // many requests in parallel. inertia router would cancel concurrent requests.

  onMounted(async () => {
    conflicts.value = await computeConflicts();
    // when there are no conflicts at all, we just emit "solved"
    if (conflicts.value.length === 0) {
      isSolved.value = true;
      emit("solved");
    }

    // conflicts with targetIsEmpty === true are not shown to
    // the user, so if there are NO conflicts of OTHER type,
    // we can solve them and emit "solved" as well...
    else if (conflicts.value.filter((c) => c.targetIsEmpty === false).length === 0) {
      solveConflicts();
    }
  });

  function getComponent(type) {
    switch (type) {
      case "degreeOfCare":
        return ComponentPatientSidebarDegreeOfCareAttribute;
      case "mobility":
        return ComponentPatientSidebarMobilityAttribute;
      case "nursingHome":
        return ComponentPatientSidebarNursingHomeAttribute;
      case "patientAddress":
        return ComponentPatientSidebarPatientAddressAttribute;
      case "tubePatient":
        return ComponentPatientSidebarTubePatientAttribute;
      case "insulinPump":
      case "palliativePatient":
      case "port":
      case "stoma":
        return ComponentPatientSidebarSimpleAttribute;
    }
  }

  /**
   * Compare two objects recursively if all their props are equal.
   */
  function deepEqual(object1, object2) {
    if (typeof object1 !== "object" || typeof object2 !== "object" || object1 === null || object2 === null) {
      return object1 === object2; // for a "leaf" (not an object), compare directly
    }

    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);

    if (keys1.length !== keys2.length) {
      return false; // not identical when different amount of keys
    }

    return keys1.every((key) => deepEqual(object1[key], object2[key])); // recursive call
  }

  async function decryptObjectValues(object) {
    if (object === null || object === undefined) {
      return null;
    }
    const entries = Object.entries(object);
    const decryptedEntries = await Promise.all(
      entries.map(async ([key, value]) => [key, await privacy.decryptValue(value)]),
    );

    return Object.fromEntries(decryptedEntries);
  }

  async function computeConflicts() {
    const masterAttributes = ["firstname", "lastname", "gender", "birthdate", "insurancenumber"];
    const labels = {
      firstname: "Vorname",
      lastname: "Nachname",
      gender: "Geschlecht",
      birthdate: "Geburtsdatum",
      insurancenumber: "Versichertennummer",
    };
    const patientAttributes = [
      "degreeOfCare",
      "mobility",
      "nursingHome",
      "patientAddress",
      "tubePatient",
      "insulinPump",
      "palliativePatient",
      "port",
      "stoma",
    ];

    let temporaryConflicts = [];

    // loop through master attributes and detect conflicts
    for (const attribute of masterAttributes) {
      let source = get(props.sourcePatient, attribute);
      let target = get(props.targetPatient, attribute);

      switch (attribute) {
        case "gender":
          source = source ? genderToHuman(source) : null;
          target = target ? genderToHuman(target) : null;
          break;
        case "birthdate":
          source = source ? dateToHuman(source) : null;
          target = target ? dateToHuman(target) : null;
          break;
        default:
          source = source ?? null;
          target = target ?? null;
          break;
      }

      if (source !== target) {
        temporaryConflicts.push({
          type: "masterAttribute",
          key: attribute,
          label: labels[attribute] ?? attribute,
          sourceIsEmpty: !source,
          targetIsEmpty: !target,
          source: source,
          target: target,
          use: target ? "target" : "source", // always prefer (existing) target, if not empty
        });
      }
    }

    // loop through patient attributes and detect conflicts
    for (const attribute of patientAttributes) {
      let sourceIsEmpty = !get(props.sourcePatient, "patient_attributes", []).some(
        (patientAttribute) => patientAttribute.type === attribute,
      );

      // simple: attribute does not exist in source, so we do not care...
      if (sourceIsEmpty) {
        // skip attribute, as it does not exist in source patient
        continue;
      }

      let targetIsEmpty = !get(props.targetPatient, "patient_attributes", []).some(
        (patientAttribute) => patientAttribute.type === attribute,
      );
      let source = get(props.sourcePatient, "patient_attributes", []).find(
        (patientAttribute) => patientAttribute.type === attribute,
      ) ?? {type: attribute, payload: {}};
      let target = get(props.targetPatient, "patient_attributes", []).find(
        (patientAttribute) => patientAttribute.type === attribute,
      ) ?? {type: attribute, payload: {}};

      // also simple: attribute does not exist in target (hidden from list, but will be created)
      if (targetIsEmpty) {
        temporaryConflicts.push({
          type: "patientAttribute",
          key: attribute,
          label: labels[attribute] ?? attribute,
          sourceIsEmpty: sourceIsEmpty,
          targetIsEmpty: targetIsEmpty,
          source: source,
          target: target,
          use: !targetIsEmpty ? "target" : "source", // always prefer (existing) target, if not empty
        });
      }

      // complex: source and target exist, so we have to compare each item within payload BUT some payloads are encrypted
      else {
        let sourcePayload = await decryptObjectValues(source.payload);
        let targetPayload = await decryptObjectValues(target.payload);

        if (!deepEqual(sourcePayload, targetPayload)) {
          temporaryConflicts.push({
            type: "patientAttribute",
            key: attribute,
            label: labels[attribute] ?? attribute,
            sourceIsEmpty: sourceIsEmpty,
            targetIsEmpty: targetIsEmpty,
            source: source,
            target: target,
            use: !targetIsEmpty ? "target" : "source", // always prefer (existing) target, if not empty
          });
        }
      }
    }

    return temporaryConflicts;
  }

  /**
   * Update existing patient and patient_attributes,
   * then emit "solved".
   * @returns {Promise<void>}
   */
  async function solveConflicts() {
    // check if we have to update the patient
    if (conflicts.value.some((conflict) => conflict.use === "source" && conflict.type === "masterAttribute")) {
      // reduce array of conflict objects to a single patient-link data object
      let data = conflicts.value
        .filter((conflict) => conflict.use === "source" && conflict.type === "masterAttribute")
        .reduce((accumulator, conflict) => {
          accumulator[conflict.key] = conflict.source;
          return accumulator;
        }, {});

      // convert birthdate from dd.mm.yyyy back to yyyy-mm-dd
      if (data.birthdate) {
        const [day, month, year] = data.birthdate.split(".");
        data.birthdate = `${year}-${month}-${day}`;
      }

      // convert gender back to internal values
      if (data.gender) {
        data.gender = genderToHuman(data.gender, true);
      }

      // data is now something like {firstname: "new firstname", birthdate: "new birthdate"}
      // now we have to pass it through encryptPatient()
      data = await privacy.encryptPatient(data);

      // data is now something like {firstname: "decrypted", firstname_clipped: "new", lastname: null, ...
      // next step: remove all data items, which are null (as we only PATCH the changed values)
      data = Object.fromEntries(Object.entries(data).filter(([, value]) => value !== null));

      // finally, PATCH existing patient
      useAuthHttp(data).patch(route("patients.update", {patient: props.targetPatient.id}));
    }

    // check if we have to update or create patient_attributes
    if (conflicts.value.some((conflict) => conflict.use === "source" && conflict.type === "patientAttribute")) {
      conflicts.value
        .filter((conflict) => conflict.use === "source" && conflict.type === "patientAttribute")
        .forEach((conflict) => {
          if (conflict.targetIsEmpty) {
            // CREATE new patient_attribute
            useAuthHttp(conflict.source).post(route("attributes.store", {patient: props.targetPatient.id}));
          } else {
            // update (PUT) existing patient_attribute
            useAuthHttp(conflict.source).put(
              route("attributes.update", {patient: props.targetPatient.id, attribute: conflict.target.id}),
            );
          }
        });
    }
    // emit solved
    isSolved.value = true;
    emit("solved");
  }

  const dateToHuman = (value) => {
    let parsedDate = /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(value)
      : /^\d{2}\.\d{2}\.\d{4}$/.test(value)
        ? new Date(value.split(".").toReversed().join("-"))
        : null;

    if (!parsedDate || Number.isNaN(parsedDate)) {
      return "";
    }

    return parsedDate.toLocaleDateString("de-DE", {dateStyle: "medium"});
  };

  const genderToHuman = (value, reverse = false) => {
    const genderMap = {
      male: "männlich",
      female: "weiblich",
      diverse: "divers",
    };

    if (reverse) {
      return Object.keys(genderMap).find((key) => genderMap[key] === value) || null;
    }

    return genderMap[value] || null;
  };

  const canTeleportButton = computed(() => {
    return !!document.querySelector("#UniversalPatientAssignmentSearchButton");
  });
</script>

<template>
  <div>
    <template v-if="isSolved">
      <div class="flex flex-col items-center justify-center py-10">
        <div class="py-4 text-lg">Wird gespeichert...</div>
        <MedSpinner size="2xl" :data-test="getTestId('solve-conflict-spinner')" />
      </div>
    </template>
    <template v-else>
      <ComponentAlert type="error" :test-id="getTestId('solve-conflicting-patient-data')">
        Die Patientendaten aus dem Import weichen von der bereits im MediCheck existierenden Patientenakte ab.
        <br />
        Bitte überprüfen Sie widersprüchliche Daten und wählen Sie aus, welche übernommen werden sollen.
      </ComponentAlert>

      <table class="w-full">
        <thead>
          <tr>
            <th class="w-1/2 pl-6"><h5 class="font-semibold">Daten aus Import</h5></th>
            <th class="w-1/2 pl-6"><h5 class="font-semibold">Aktuell im MediCheck</h5></th>
          </tr>
        </thead>

        <tbody>
          <template v-for="(conflict, index) in conflicts" :key="index">
            <tr v-if="conflict.type === 'masterAttribute'" class="[&>td]:content-start [&>td]:border-t-2 [&>td]:py-2">
              <td>
                <label
                  :for="`conflict-${index}-source`"
                  :class="
                    cn('items-top flex space-x-2', {
                      'cursor-pointer': !conflict.sourceIsEmpty,
                      'cursor-not-allowed!': conflict.sourceIsEmpty,
                    })
                  "
                >
                  <input
                    :id="`conflict-${index}-source`"
                    v-model="conflict.use"
                    :disabled="conflict.sourceIsEmpty"
                    type="radio"
                    class="mt-0.5 text-mcgreen! ring-red-500! ring-offset-0! focus-visible:border-mcred focus-visible:ring disabled:bg-gray-300 disabled:opacity-60"
                    value="source"
                  />

                  <div class="leading-4">
                    <div class="text-xs text-gray-600">
                      {{ conflict.label }}
                      <span v-if="conflict.use === 'source'" class="text-xs font-semibold text-mcgreen normal-case">
                        wird übernommen
                      </span>
                    </div>
                    <div>{{ conflict.source ?? "–" }}</div>
                  </div>
                </label>
              </td>

              <td>
                <!-- no disabled, it's always possible to keep existing empty value -->
                <label :for="`conflict-${index}-target`" class="items-top flex cursor-pointer space-x-2">
                  <input
                    :id="`conflict-${index}-target`"
                    v-model="conflict.use"
                    type="radio"
                    class="mt-0.5 text-mcgreen! ring-red-500! ring-offset-0! focus-visible:border-mcred focus-visible:ring disabled:bg-gray-300 disabled:opacity-60"
                    value="target"
                  />

                  <div class="leading-4">
                    <div class="text-xs text-gray-600">
                      {{ conflict.label }}
                      <span v-if="conflict.use === 'target'" class="text-xs font-semibold text-mcgreen normal-case">
                        wird beibehalten
                      </span>
                    </div>
                    <div>{{ conflict.target ?? "–" }}</div>
                  </div>
                </label>
              </td>
            </tr>

            <tr v-else-if="!conflict.targetIsEmpty" class="[&>td]:content-start [&>td]:border-t-2 [&>td]:py-2">
              <td>
                <label
                  :for="`conflict-attr-${index}-source`"
                  :class="
                    cn('items-top flex cursor-pointer space-x-2', {
                      'cursor-pointer': !conflict.sourceIsEmpty,
                      'cursor-not-allowed!': conflict.sourceIsEmpty,
                    })
                  "
                >
                  <input
                    :id="`conflict-attr-${index}-source`"
                    v-model="conflict.use"
                    :disabled="conflict.sourceIsEmpty"
                    type="radio"
                    class="mt-0.5 text-mcgreen! ring-red-500! ring-offset-0! focus-visible:border-mcred focus-visible:ring disabled:bg-gray-300 disabled:opacity-60"
                    value="source"
                  />

                  <component
                    :is="getComponent(conflict.source?.type)"
                    :attribute="conflict.source"
                    :patient="sourcePatient"
                  >
                    <template #label>
                      <span v-if="conflict.use === 'source'" class="text-xs font-semibold text-mcgreen normal-case">
                        wird übernommen
                      </span>
                    </template>
                    <template v-if="conflict.sourceIsEmpty" #default>&ndash;</template>
                  </component>
                </label>
              </td>

              <td>
                <label :for="`conflict-attr-${index}-target`" class="items-top flex cursor-pointer space-x-2">
                  <!-- no disabled, always possible to keep existing empty value -->
                  <input
                    :id="`conflict-attr-${index}-target`"
                    v-model="conflict.use"
                    type="radio"
                    class="mt-0.5 text-mcgreen! ring-red-500! ring-offset-0! focus-visible:border-mcred focus-visible:ring disabled:bg-gray-300 disabled:opacity-60"
                    value="target"
                  />

                  <component
                    :is="getComponent(conflict.target?.type)"
                    :attribute="conflict.target"
                    :patient="targetPatient"
                  >
                    <template #label>
                      <span v-if="conflict.use === 'target'" class="text-xs font-semibold text-mcgreen normal-case">
                        wird beibehalten
                      </span>
                    </template>

                    <template v-if="conflict.targetIsEmpty" #default>&ndash;</template>
                  </component>
                </label>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </template>

    <div class="flex flex-row-reverse items-end justify-between">
      <Teleport v-if="canTeleportButton" to="#UniversalPatientAssignmentSearchButton">
        <MedButton
          :data-test="getTestId('solve-conflicting-patient-data-confirm-button')"
          :disabled="isSolved"
          variant="primary"
          @click="solveConflicts"
        >
          Bestätigen
        </MedButton>
      </Teleport>

      <template v-else>
        <MedButton
          :disabled="isSolved"
          variant="primary"
          :data-test="getTestId('solve-conflicting-patient-data-confirm-button')"
          @click="solveConflicts"
        >
          Bestätigen
        </MedButton>
      </template>
    </div>
  </div>
</template>
